// packages/storefront/src/translation/domGuardState.ts
//
// The DOM guard's shared state (docs/embed-browser-translation.md). One page can run bundles from several deploys
// (storefront, chat, reviews; cached and fresh), and they share ONE registry: the newest impl adopts the state an
// older impl built, so a widget translated before an upgrade keeps healing after it. Both shapes below are therefore
// APPEND-ONLY: a field is never renamed, retyped or removed, and a new field is only ever added at the end with a
// default in `initDomGuardState` (domGuard.install.test.ts pins the field list as a prefix).
//
// Any change to this file bumps DOM_GUARD_IMPL_VERSION (domGuard.ts, version rule 2).

/** Counters, all present from the start (telemetry reads them as numbers). Append-only like the state. */
export interface DomGuardStats {
	groups: number;
	rebuilds: number;
	healRemove: number;
	healInsert: number;
	unmappedRemove: number;
	unmappedInsert: number;
	unmappedText: number;
	reissued: number;
	staleRestores: number;
	repairs: number;
	repairGiveUp: number;
	islandRestores: number;
	islandCopies: number;
	inversionFailed: number;
	mixed: number;
	faults: number;
	autoObserved: number;
	lostPosition: number;
}

export const DOM_GUARD_STATS_KEYS = [
	'groups',
	'rebuilds',
	'healRemove',
	'healInsert',
	'unmappedRemove',
	'unmappedInsert',
	'unmappedText',
	'reissued',
	'staleRestores',
	'repairs',
	'repairGiveUp',
	'islandRestores',
	'islandCopies',
	'inversionFailed',
	'mixed',
	'faults',
	'autoObserved',
	'lostPosition',
] as const satisfies readonly (keyof DomGuardStats)[];

/** What the page-level event listeners receive (see DomGuardImpl.onEvent). */
export type DomGuardEventKind =
	| 'unmapped-remove'
	| 'unmapped-insert'
	| 'unmapped-text'
	| 'lost-position'
	| 'element-lost-repeatedly'
	| 'island-rewritten-repeatedly'
	| 'not-on-top'
	| 'no-react-key'
	| 'breaker';

export interface DomGuardEvent {
	kind: DomGuardEventKind;
	implVersion: number;
	stats: DomGuardStats;
	/** Structural facts only (a tag name, a member name): never text content. */
	detail?: Record<string, string>;
}

/**
 * A run of one parent's children that a page translator rewrote. `members` are React's nodes of that run in React's
 * order; each text member may have its own display (itself, a translator node that replaced it 1:1, or our clone).
 * `nodes` are the non-member nodes currently shown in the run (translator output, displays being replaced).
 */
export interface DomGuardGroup {
	id: number;
	parent: Node;
	members: Node[];
	nodes: Set<Node>;
	prevAnchor: Node | null;
	nextAnchor: Node | null;
	/** Text members React wrote since the last rebuild (shown by a fresh clone unless the write is on screen). */
	changed: Set<Node>;
	/** Text members React re-placed itself after a translator displaced them: always shown by a fresh clone. */
	forceClone: Set<Node>;
	dirty: boolean;
	dead: boolean;
}

/** A slot a displaced node occupied: the fallback anchor when React inserts before it and no group maps it. */
export interface DomGuardSlot {
	parent: Node;
	next: Node | null;
}

/** Repair / restore accounting for one element within the 10 s window. */
export interface DomGuardRateWindow {
	n: number;
	t: number;
}

export interface DomGuardFault {
	where: string;
	message: string;
}

export interface DomGuardState {
	/** The breaker tripped: safe mode (no healing; our invalid calls become no-throw). */
	off: boolean;
	/** A translator has been seen at least once on this page. */
	active: boolean;
	/** `'__reactFiber$<k>'` of each registered react-dom copy. */
	keys: string[];
	/** Observed targets. */
	covered: WeakSet<Node>;
	/** Our nodes removed/moved by a React-classified call since the last observer delivery. */
	viaCall: Set<Node>;
	viaCallClearQueued: boolean;
	/** Foreign node -> our node it was inserted before THROUGH a trampoline (this microtask). */
	callInsBefore: Map<Node, Node>;
	callInsClearQueued: boolean;
	/** R1: foreign S -> tracked T it was inserted directly before. */
	insertedBefore: WeakMap<Node, Node>;
	/** Displaced node -> { parent, next } at displacement (fallback anchor). */
	slot: WeakMap<Node, DomGuardSlot>;
	/** Texts React wrote while on screen (a translator may hold their old text). */
	writtenInPlace: WeakSet<Node>;
	/** React text -> the last value React wrote. */
	reactValue: WeakMap<Node, string>;
	/** React text -> its value before a translator rewrote it in place. */
	truth: WeakMap<Node, string>;
	/** Our clone -> the text it stands for. */
	origin: WeakMap<Node, Node>;
	/** Text -> our current clone of it. */
	cloneOf: WeakMap<Node, Node>;
	/** Tracked node -> foreign node that replaced it 1:1. */
	standIn: WeakMap<Node, Node>;
	/** React node -> its group. */
	memberOf: WeakMap<Node, DomGuardGroup>;
	/** Non-member node shown in a group's region -> the group. */
	regionOf: WeakMap<Node, DomGuardGroup>;
	/** Element -> repair accounting. */
	lost: WeakMap<Node, DomGuardRateWindow>;
	/** Island -> restore accounting. */
	restores: WeakMap<Node, DomGuardRateWindow>;
	pendingGroups: Set<DomGuardGroup>;
	pendingWrites: Set<Node>;
	pendingLone: Map<Node, Node>;
	pendingIslands: Set<Element>;
	pendingCopies: Set<Element>;
	scheduled: boolean;
	seq: number;
	listeners: ((e: DomGuardEvent) => void)[];
	emitted: Set<string>;
	stats: DomGuardStats;
	lastFaults: DomGuardFault[];
	/**
	 * The keys of `keys` that were registered for a SUBTREE only (`registerReactSubtree`, impl v3): a framework's root
	 * over the whole document, whose page around the subtree is never observed. A key also registered as a root of
	 * its own is not here.
	 */
	subtreeKeys: string[];
}

/** The frozen v1 field order. Append new fields at the END of this list, of the interface and of initDomGuardState. */
export const DOM_GUARD_STATE_FIELDS = [
	'off',
	'active',
	'keys',
	'covered',
	'viaCall',
	'viaCallClearQueued',
	'callInsBefore',
	'callInsClearQueued',
	'insertedBefore',
	'slot',
	'writtenInPlace',
	'reactValue',
	'truth',
	'origin',
	'cloneOf',
	'standIn',
	'memberOf',
	'regionOf',
	'lost',
	'restores',
	'pendingGroups',
	'pendingWrites',
	'pendingLone',
	'pendingIslands',
	'pendingCopies',
	'scheduled',
	'seq',
	'listeners',
	'emitted',
	'stats',
	'lastFaults',
	// impl v3
	'subtreeKeys',
] as const satisfies readonly (keyof DomGuardState)[];

// Compile-time: the field list above names every field of DomGuardState.
const _everyStateFieldListed: [Exclude<keyof DomGuardState, (typeof DOM_GUARD_STATE_FIELDS)[number]>] extends [never] ? true : false = true;

function isCompleteState(s: Partial<DomGuardState>): s is DomGuardState {
	return DOM_GUARD_STATE_FIELDS.every((k) => k in s);
}

/**
 * Adds every missing field to `s` IN PLACE and never replaces an existing one: `s` may be the state an older impl
 * built on this page, and it keeps its groups, keys and listeners. Returns the same object.
 */
export function initDomGuardState(s: Partial<DomGuardState>): DomGuardState {
	const def = <K extends keyof DomGuardState>(k: K, v: DomGuardState[K]) => {
		if (!(k in s)) s[k] = v;
	};
	def('off', false);
	def('active', false);
	def('keys', []);
	def('covered', new WeakSet());
	def('viaCall', new Set());
	def('viaCallClearQueued', false);
	def('callInsBefore', new Map());
	def('callInsClearQueued', false);
	def('insertedBefore', new WeakMap());
	def('slot', new WeakMap());
	def('writtenInPlace', new WeakSet());
	def('reactValue', new WeakMap());
	def('truth', new WeakMap());
	def('origin', new WeakMap());
	def('cloneOf', new WeakMap());
	def('standIn', new WeakMap());
	def('memberOf', new WeakMap());
	def('regionOf', new WeakMap());
	def('lost', new WeakMap());
	def('restores', new WeakMap());
	def('pendingGroups', new Set());
	def('pendingWrites', new Set());
	def('pendingLone', new Map());
	def('pendingIslands', new Set());
	def('pendingCopies', new Set());
	def('scheduled', false);
	def('seq', 0);
	def('listeners', []);
	def('emitted', new Set());
	def('stats', zeroStats());
	def('lastFaults', []);
	def('subtreeKeys', []);
	const stats = s.stats;
	if (stats !== undefined) for (const k of DOM_GUARD_STATS_KEYS) if (!(k in stats)) stats[k] = 0;
	if (!isCompleteState(s)) throw new Error('initDomGuardState: a DomGuardState field has no default');
	return s;
}

function zeroStats(): DomGuardStats {
	return {
		groups: 0,
		rebuilds: 0,
		healRemove: 0,
		healInsert: 0,
		unmappedRemove: 0,
		unmappedInsert: 0,
		unmappedText: 0,
		reissued: 0,
		staleRestores: 0,
		repairs: 0,
		repairGiveUp: 0,
		islandRestores: 0,
		islandCopies: 0,
		inversionFailed: 0,
		mixed: 0,
		faults: 0,
		autoObserved: 0,
		lostPosition: 0,
	};
}
